import type {
  BuyerImportColumnPlanDto,
  BuyerSmartImportTabSelectionDto,
} from "@/api/buyerSmartImport";
import { Input, StatusBadge, SubTable, type SubTableColumn } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { smartImportCopy } from "./smartImportCopy";
import { parseCommaList } from "./smartImportReview";
import {
  BUYER_IMPORT_TARGETS,
  BUYER_IMPORT_TARGET_LABELS,
  BUYER_IMPORT_TRANSFORM_LABELS,
  isLowConfidence,
  legalTransformsFor,
  MAX_PURCHASE_PRICE_HINT,
  type BuyerImportTarget,
  type BuyerImportTransform,
} from "./smartImportVocabulary";

/**
 * One tab's mapping, one row per source column (D4).
 *
 * <p>Every row carries the column's own header, three sample values, the target
 * and transform pickers, the sentinels, the model's reason and a low-confidence
 * marker — so the operator can judge a mapping without opening the file, which
 * is the only thing standing between a confident wrong guess and a column of
 * budgets written into the qualified-amount field.</p>
 */

export interface SmartImportMappingTableProps {
  tab: BuyerSmartImportTabSelectionDto;
  /**
   * The tab's first rows, RAW — index 0 is raw row 1, header row included.
   * The data rows are sliced from it here, off the tab's own
   * `headerRowIndex`, so moving the header row re-reads the samples with no
   * second upload.
   */
  sampleRows?: readonly string[][] | null;
  onChangeTarget: (columnIndex: number, target: string) => void;
  onChangeTransform: (columnIndex: number, transform: string) => void;
  onChangeSentinels: (columnIndex: number, sentinels: string[]) => void;
  onChangeFormat: (columnIndex: number, format: string) => void;
  disabled?: boolean;
}

/** How many sample values a mapping row shows (D4). */
export const MAPPING_SAMPLE_COUNT = 3;

/**
 * The first {@link MAPPING_SAMPLE_COUNT} DATA values for one column.
 *
 * <p>🔴 <b>Sliced off `headerRowIndex`, and blanks are KEPT.</b> The array
 * arrives raw with the header row in it, so the data begins at index
 * `headerRowIndex` — index 0 being raw row 1 and the index being 1-based.
 * Dropping blank cells and showing whatever non-blank values could be found
 * further down would tell the operator a column is populated when it is mostly
 * empty, which is the opposite of what three samples are for: a column of three
 * em dashes is a finding.</p>
 */
export function samplesForColumn(
  sampleRows: readonly string[][] | null | undefined,
  headerRowIndex: number,
  columnIndex: number,
): string[] {
  if (!sampleRows || sampleRows.length === 0) return [];
  const firstDataRow = Math.max(0, headerRowIndex);
  return sampleRows
    .slice(firstDataRow, firstDataRow + MAPPING_SAMPLE_COUNT)
    .map((row) => row[columnIndex] ?? "");
}

export function SmartImportMappingTable({
  tab,
  sampleRows,
  onChangeTarget,
  onChangeTransform,
  onChangeSentinels,
  onChangeFormat,
  disabled,
}: SmartImportMappingTableProps) {
  const columns: SubTableColumn<BuyerImportColumnPlanDto>[] = [
    {
      key: "column",
      header: smartImportCopy.columnHeader,
      render: (c) => (
        <div className="flex flex-col gap-space-1">
          <span className="font-medium text-onyx">{c.header || `Column ${c.index + 1}`}</span>
          {isLowConfidence(c.confidence) && (
            <StatusBadge tone="warn" data-testid={`mapping-low-confidence-${c.index}`}>
              {smartImportCopy.lowConfidence}
            </StatusBadge>
          )}
        </div>
      ),
    },
    {
      key: "samples",
      header: smartImportCopy.samplesHeader,
      render: (c) => {
        const samples = samplesForColumn(sampleRows, tab.headerRowIndex, c.index);
        if (samples.length === 0) {
          return <span className="text-caption text-gray-mid">{smartImportCopy.noSamples}</span>;
        }
        return (
          <ul className="flex flex-col gap-space-1 text-caption text-gray-mid">
            {samples.map((value, i) => (
              <li key={`${c.index}-sample-${i}`} className="font-mono break-all">
                {value.trim() === "" ? "—" : value}
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      key: "target",
      header: smartImportCopy.targetHeader,
      render: (c) => (
        <Select
          aria-label={`Maps to, for ${c.header || `column ${c.index + 1}`}`}
          data-testid={`mapping-target-${c.index}`}
          className="text-small"
          disabled={disabled}
          value={c.target}
          onChange={(e) => onChangeTarget(c.index, e.target.value)}
        >
          {targetOptions(c.target).map((t) => (
            <option key={t} value={t}>
              {BUYER_IMPORT_TARGET_LABELS[t as BuyerImportTarget] ?? t}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: "transform",
      header: smartImportCopy.transformHeader,
      render: (c) => (
        <div className="flex flex-col gap-space-1">
          <Select
            aria-label={`Read as, for ${c.header || `column ${c.index + 1}`}`}
            data-testid={`mapping-transform-${c.index}`}
            className="text-small"
            disabled={disabled}
            value={c.transform}
            onChange={(e) => onChangeTransform(c.index, e.target.value)}
          >
            {transformOptions(c.target, c.transform).map((t) => (
              <option key={t} value={t}>
                {BUYER_IMPORT_TRANSFORM_LABELS[t as BuyerImportTransform] ?? t}
              </option>
            ))}
          </Select>
          {c.transform === "dateText" && (
            <Input
              aria-label={`${smartImportCopy.formatLabel}, for ${c.header || `column ${c.index + 1}`}`}
              data-testid={`mapping-format-${c.index}`}
              className="text-small"
              disabled={disabled}
              placeholder="M/d/yy h:mmtt"
              value={c.format ?? ""}
              onChange={(e) => onChangeFormat(c.index, e.target.value)}
            />
          )}
          {c.target === "MaxPurchasePrice" && (
            <span className="text-caption text-gray-mid">{MAX_PURCHASE_PRICE_HINT}</span>
          )}
        </div>
      ),
    },
    {
      key: "sentinels",
      header: smartImportCopy.sentinelsHeader,
      render: (c) => (
        <Input
          aria-label={`Treated as blank, for ${c.header || `column ${c.index + 1}`}`}
          data-testid={`mapping-sentinels-${c.index}`}
          className="text-small"
          disabled={disabled}
          placeholder={smartImportCopy.sentinelsPlaceholder}
          value={c.sentinels.join(", ")}
          onChange={(e) => onChangeSentinels(c.index, parseSentinels(e.target.value))}
        />
      ),
    },
    {
      key: "reason",
      header: smartImportCopy.reasonHeader,
      render: (c) => (
        <span className="text-caption text-gray-mid">{c.reason?.trim() || "—"}</span>
      ),
    },
  ];

  return (
    <SubTable
      caption={`Column mapping for ${tab.tabName}`}
      data-testid={`mapping-table-${tab.tabName}`}
      columns={columns}
      rows={tab.columns}
      rowKey={(c) => String(c.index)}
      rowTestId={(c) => `mapping-row-${c.index}`}
      /*
        A mapping table is an approval surface, not a list to browse: the
        operator is signing off the WHOLE mapping, so paging it at ten would
        hide columns behind a control they have no reason to press, and a
        sort would put the rows in an order that no longer matches the file
        they are checking against.
      */
      chromeThreshold={Infinity}
      empty={{ message: "This tab has no columns." }}
    />
  );
}

/**
 * The target dropdown's options.
 *
 * <p>🔴 A value the closed vocabulary does not know is KEPT at the head of the
 * list rather than dropped. A `<select>` whose value matches no option renders
 * blank, so silently omitting an unknown target would show the operator an
 * empty control on the one row that most needs their attention — the column the
 * server has already warned it is dropping to Notes (X3).</p>
 */
export function targetOptions(current: string): string[] {
  const known = BUYER_IMPORT_TARGETS as readonly string[];
  return known.includes(current) ? [...known] : [current, ...known];
}

/** The same rule for transforms, over only those legal for the target. */
export function transformOptions(target: string, current: string): string[] {
  const legal = legalTransformsFor(target) as readonly string[];
  return legal.includes(current) ? [...legal] : [current, ...legal];
}

/**
 * `"n/a, none"` → `["n/a", "none"]`.
 *
 * <p>The same parse the interests table uses for industries and geographies —
 * see {@link parseCommaList}. Named for the domain here so the call site reads
 * as what it is.</p>
 */
export function parseSentinels(raw: string): string[] {
  return parseCommaList(raw);
}
